<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive, onUnmounted, computed, nextTick } from "vue";
import { isPhone } from "@/DeviceInfoPlugin";
import Constants from "../../Constants";
import CommonVTimeLineDay from "@/components/common/VTimeLineDay.vue";
import { BaseSetup, mountedBase } from "@/DataLoaderStatic";
import {
  getSperrDate,
  gotoDate,
  mountedTimeline,
  TimelineCarouselParameter,
  TimeLineCarouselUIData,
} from "./TimelineCarouselFunctions";

const props = defineProps<{
  queryEdit?: string;
  daysBack: number;
  daysForward: number;
  goDate?: Date | null;
  showName?: boolean;
  textKennung?: boolean;

  showTagessaldo?: boolean;
  showNacherfassung?: boolean;
  showProjektstunden?: boolean;
  showReisekosten?: boolean;
  showError?: boolean;
  query?: string;
  showManZulagen?: boolean;
}>();
const emit = defineEmits(["back", "gotoDate"]);

const pageParams: TimelineCarouselParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "",
  queryEdit: "",
  queryAIC: AUApi.Current.user.aic,
  aic: AUApi.Current.user.aic,
  emitBack: () => emit("back"),

  showTagessaldo: props.showTagessaldo,
  showNacherfassung: props.showNacherfassung,
  showProjektstunden: props.showProjektstunden,
  showReisekosten: props.showReisekosten,
  showManZulagen: props.showManZulagen,
};
const windowWidth = ref(window.innerWidth);
const windowHeight = ref(window.innerHeight - 100);
const markerspan = ref<HTMLElement | null>(null);

let componentKey = ref<number>(0);

const uiData = reactive<TimeLineCarouselUIData>(
  BaseSetup<TimelineCarouselParameter, TimeLineCarouselUIData>(pageParams, {
    today: new Date(),
  })
);
onMounted(async () => {
  if (Constants.isMobile && isPhone()) {
    const vh = window.visualViewport?.height ?? window.innerHeight;

    const safeBottom = Constants.platformMac ? 35 : 0;
    windowHeight.value = Math.floor(vh - 100 - safeBottom);
  }
  window.addEventListener("resize", updateSize);
  pageParams.query = props.query;
  await mountedBase(uiData, pageParams, false);

  mountedTimeline(uiData, props, calcDates);

  if (!uiData.scrollToDate) {
    uiData.scrollToDate = new Date();
  }
  if (props.goDate) {
    uiData.scrollToDate = new Date(props.goDate);
    gotoDate(props.goDate, uiData);
  }
  uiData.showCarousel = uiData.initialized = true;
});

const updateSize = () => {
  if (windowWidth.value !== window.innerWidth) {
    windowWidth.value = window.innerWidth;
    setSizes();
  }
};
function setSizes() {
  const containerWidth =
    markerspan.value?.parentElement?.getBoundingClientRect().width ?? window.innerWidth;

  if (Constants.isMobile && isPhone()) {
    uiData.maxWidth = containerWidth;
    uiData.minWidth = containerWidth;
  } else {
    const horizontalPadding = 40;
    uiData.maxWidth = Math.max(200, Math.floor(containerWidth - horizontalPadding));
    uiData.minWidth = uiData.maxWidth;
  }
  nextTick(() => onRefresh());
}
onUnmounted(() => {
  window.removeEventListener("resize", updateSize);
});

function calcDates() {
  const validDaysBack = calcDaysBack();
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  let daysForward = props.daysForward;
  for (let i = validDaysBack * -1; i <= daysForward; i++) {
    uiData.dates.push(startDate.addDays(i));
  }
}

function calcDaysBack() {
  // periodensperre checken
  let sperrDate = getSperrDate(uiData, pageParams);
  if (sperrDate) {
    uiData.minDate = sperrDate.toISOStringWithTZ().substr(0, 10);
    // 7.11.2025 ich darf keinen Tag abziehen sonst ein Tag zuviel!
    // sperrDate = sperrDate.addDays(-1);
    // 16.3.2022 sperrdate 1 tag abziehen - sonst rechne ich zu kurz - statt 1.3 - 2.3
    console.log("sperre: " + JSON.stringify(sperrDate));
    // +1 weg - sonst wird um 1 Tag zu weit gerechnet Isabelle
    const diff = Math.round(
      (uiData.today.valueOf() - sperrDate.valueOf()) / (1000 * 60 * 60 * 24)
    );
    return Math.min(Math.abs(diff), props.daysBack ?? 7);
  }
  return props.daysBack;
}

function onRefresh() {
  uiData.showCarousel = false;
  mountedTimeline(uiData, props, calcDates);
  componentKey.value += 1;
  uiData.showCarousel = true;
}
function onTabChange(tab: number) {
  if (!tab) {
    // @change wird bei einer gewissen größe schon beim Aufbau aufgerufen
    // da gibt es noch gar kein eingestelltes tab - daher muss dann auf HEUTE positioniert werden!
    uiData.scrollToDate = new Date();
    return;
  }
  uiData.scrollToDate = uiData.dates[tab * uiData.timelinesInPage + 1];
  emit("gotoDate", uiData.scrollToDate);
}
function gotoDateNow(date: Date | string) {
  gotoDate(date, uiData);
  emit("gotoDate", date);
}
const nextDisabled = computed(() => {
  const active = uiData.activeTab ?? 0;
  const timelinesInPage = uiData.timelinesInPage ?? 1;
  const datesLen = (uiData.dates ?? []).length;
  if (datesLen === 0) return true;
  const lastVisibleIndex = Math.min(
    datesLen - 1,
    active * timelinesInPage + timelinesInPage
  );

  return lastVisibleIndex >= datesLen - 1;
});

const prevDisabled = computed(() => {
  const active = uiData.activeTab ?? 0;
  return active <= 0;
});
function prevPage() {
  if (!prevDisabled.value) {
    uiData.activeTab = (uiData.activeTab ?? 0) - 1;
    // optional emit
    onTabChange(uiData.activeTab ?? 0);
  }
}
function nextPage() {
  if (!nextDisabled.value) {
    uiData.activeTab = (uiData.activeTab ?? 0) + 1;
    onTabChange(uiData.activeTab ?? 0);
  }
}
</script>

<template>
  <v-sheet class="full-height ios-safe carousel-wrap">
    <span ref="markerspan"></span>
    <v-progress-linear clor="dvhSlider" v-if="!uiData.showCarousel" indeterminate />
    <v-carousel
      light
      v-if="uiData.showCarousel"
      :show-arrows="false"
      hide-delimiters
      v-model="uiData.activeTab"
      :height="windowHeight"
      :continuous="false"
      :key="componentKey"
      @update:modelValue="onTabChange"
    >
      <v-carousel-item
        :width="uiData.maxWidth"
        :min-width="uiData.minWidth"
        light
        :key="`page-${i}`"
        v-for="i in uiData.pages"
        class="full-height"
      >
        <v-layout :height="windowHeight">
          <v-card
            :key="j"
            v-for="j in uiData.timelinesInPage"
            class="pa-0 pt-2 pl-2 ma-0"
            :width="uiData.minWidth ?? 400"
          >
            <CommonVTimeLineDay
              v-if="uiData.dates.length > uiData.timelinesInPage * (i - 1) + j"
              :query="pageParams.query"
              :contentHeight="windowHeight"
              :contentWidth="uiData.myWidth"
              :day="uiData.dates[uiData.timelinesInPage * (i - 1) + j]"
              ref="timeline"
              :showError="props.showError"
              :showTagessaldo="pageParams.showTagessaldo"
              :showNacherfassung="pageParams.showNacherfassung"
              :showManZulagen="pageParams.showManZulagen"
              :queryEdit="pageParams.queryEdit"
              :showProjektstunden="pageParams.showProjektstunden"
              :showReisekosten="pageParams.showReisekosten"
              @reload="onRefresh"
              :aic="pageParams.aic"
              :showName="showName"
              :nameUebergabe="uiData.nameUebergabe"
              :admin="pageParams.admin"
              @goToDate="gotoDateNow"
              :minDate="uiData.minDate"
            />
          </v-card>
        </v-layout>
      </v-carousel-item>
    </v-carousel>

    <v-btn
      v-if="!prevDisabled"
      icon="mdi-chevron-left"
      class="carousel-btn prev-btn green-btn"
      :style="{ top: '50%', transform: 'translateY(-50%)' }"
      @click="prevPage"
    />
    <v-btn
      v-if="!nextDisabled"
      icon="mdi-chevron-right"
      class="carousel-btn next-btn green-btn"
      :style="{ top: '50%', transform: 'translateY(-50%)' }"
      @click="nextPage"
    />
  </v-sheet>
</template>
<style scoped>
.carousel-wrap {
  position: relative;
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 30;
  transition: top 200ms ease, bottom 200ms ease, transform 200ms ease;
}

.carousel-btn:active {
  top: 50% !important;
  transform: translateY(-50%) !important;
}

.prev-btn {
  left: 10px;
}

.next-btn {
  right: 10px;
}

@media (max-width: 600px) {
  .carousel-btn {
    top: auto;
    bottom: 10px;
    transform: none;
  }
  .prev-btn,
  .next-btn {
    z-index: 40;
  }
}
.full-height {
  height: 100svh;
  min-height: 100svh;
}
.v-carousel,
.v-carousel-item,
.v-layout {
  height: 100%;
}
:root {
  --safe-bottom: env(safe-area-inset-bottom, 0px);
}
.ios-safe {
  padding-bottom: calc(var(--safe-bottom) + 12px);
}
.green-btn {
  background-color: rgba(42, 43, 42, 0.15);
  color: #606060;
}

.green-btn:hover {
  background-color: rgba(30, 30, 30, 0.25);
}
</style>
